/**
 * The one signal the transport raises that a screen cannot deal with on its own.
 *
 * When RigoSub reports an organisation SUSPENDED, `SubscriptionGateMiddleware` refuses every
 * `/api/v1/*` call with **402 Payment Required** and the problem type below. Nothing a screen can
 * do with that answer is right: a list would render an error toast, a form would report a failed
 * save, and the customer would be left guessing across a dozen of them. So the transport announces
 * it once and the shell replaces itself with the screen that explains it.
 *
 * A module-level emitter rather than something threaded through the query client, because the
 * refusal can arrive on any call from any screen, including one made before React has mounted
 * anything that could hold the state.
 */

/** The problem `type` the API sends with its 402. Mirrors `SubscriptionGateMiddleware.ProblemType`. */
export const SUSPENDED_PROBLEM_TYPE = "https://rigo.dev/problems/subscription_suspended";

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Whether a response is the subscription gate's refusal.
 *
 * The status code is the test and the type is the confirmation, in that order: 402 has exactly one
 * producer in this API, and a body that failed to parse must not turn the block into a toast.
 */
export function isSuspendedResponse(status: number, type: string | undefined): boolean {
  return status === 402 && (type === undefined || type === SUSPENDED_PROBLEM_TYPE);
}

/** Subscribes to the refusal. Returns the unsubscribe. */
export function onSubscriptionSuspended(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Announces the refusal. Called by the HTTP client, and by nothing else. */
export function notifySubscriptionSuspended(): void {
  // Iterating the set itself is safe even if a listener unsubscribes while being called: a `Set`
  // iterator tolerates deletion of entries it has already visited.
  for (const listener of listeners) listener();
}
